import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, DollarSign, Filter, X, EyeOff, Plus, Minus, Edit, CheckSquare, Square, Maximize2, Minimize2, Layout, Loader2, Check, ArrowRightLeft, Maximize, Minimize } from 'lucide-react';
import ForecastAccountDetail from './ForecastAccountDetail';
import DailyForecastModal from './DailyForecastModal';
import BulkDriverEditModal from './BulkDriverEditModal';
import { getForecastPnLData, getForecastPnLDataV2 } from '../../utils/forecastPnLApi';
import { getFinancialForecasts, createFinancialForecast, clearForecastMonth } from '../../utils/forecastApi';
import { upsertBulkForecastAccounts } from '../../utils/forecastAccountsApi';
import { calculateForecastAmount, getPreviousMonthIndex } from '../../utils/forecastCalculations';
import { formatCurrencyRounded } from '../../utils/currency';

const PnLForecastView = ({
  chartOfAccounts = [],
  forecastData = [],
  budgetData = [],
  forecastAccounts = {},
  orgId,
  locationId,
  currency = 'USD',
  year,
  onDataChange,
  initialStartMonth = 0,
  initialEndMonth = 11,
  refreshKey = 0,
  isRefreshing = false, // Background refresh in progress (don't show loading screen)
  onRefreshComplete // Callback when refresh completes
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [forecastDetailData, setForecastDetailData] = useState({});
  const [cosmosForecasts, setCosmosForecasts] = useState({});
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false); // Track if initial load complete
  const [editingCell, setEditingCell] = useState(null); // { accountId, monthIndex }
  const [localEdits, setLocalEdits] = useState({}); // { `${accountId}_${monthIndex}`: value }
  const [savingCells, setSavingCells] = useState(new Set());
  const [savedCells, setSavedCells] = useState(new Set()); // Track cells that were successfully saved
  const [showDailyModal, setShowDailyModal] = useState(false);
  const [dailyModalAccount, setDailyModalAccount] = useState(null);
  const [dailyModalMonth, setDailyModalMonth] = useState(null);
  const [startMonth, setStartMonth] = useState(initialStartMonth); // 0-11 for Jan-Dec
  const [endMonth, setEndMonth] = useState(initialEndMonth); // 0-11 for Jan-Dec
  const [hideZeroBalances, setHideZeroBalances] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [expandedColumns, setExpandedColumns] = useState({}); // Track which month columns are expanded
  const [selectedAccountIds, setSelectedAccountIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false); // Toggle checkbox mode
  const [density, setDensity] = useState('comfortable'); // 'compact', 'comfortable'
  const [isFullScreen, setIsFullScreen] = useState(false);
  const saveTimerRef = useRef({});

  // Sync date range from parent when it changes (only if different)
  useEffect(() => {
    if (startMonth !== initialStartMonth) {
      setStartMonth(initialStartMonth);
    }
    if (endMonth !== initialEndMonth) {
      setEndMonth(initialEndMonth);
    }
  }, [initialStartMonth, initialEndMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch ALL forecast detail data AND SQL forecast data
  useEffect(() => {
    const fetchAllForecastDetails = async () => {
      if (!orgId || !locationId || !year) {
        setIsLoadingDetails(false);
        return;
      }

      // Only show loading screen on initial load, not during background refresh
      if (!hasInitiallyLoaded) {
        setIsLoadingDetails(true);
      }

      try {
        // Fetch V2 API data (with pre-calculated forecasts) and SQL forecast data in parallel
        console.log('Fetching P&L data (V2) and SQL forecasts for all accounts...');
        const [allData, allForecasts] = await Promise.all([
          getForecastPnLDataV2({ orgId, locationId, year }),
          getFinancialForecasts({ orgId, locationId, year })
        ]);
        console.log(`Received ${allData.length} forecast detail records and ${allForecasts.length} forecast records`);

        // Build a map of accountId -> monthlyData from SQL view
        const detailsMap = {};
        allData.forEach(entry => {
          const accountId = entry.chartOfAccountId;
          const monthIndex = entry.monthNumber - 1; // Convert 1-12 to 0-11

          if (!detailsMap[accountId]) {
            detailsMap[accountId] = {};
          }

          detailsMap[accountId][monthIndex] = entry;
        });

        // Build a map of accountId -> year -> monthlyData from SQL forecast table
        // SQL structure: Each row is one month with forecastDate, forecastAmount, driverType, driverInput
        const sqlForecastsMap = {};
        allForecasts.forEach(forecastRow => {
          const accountId = forecastRow.chartOfAccountId;
          const forecastDate = new Date(forecastRow.forecastDate);
          const rowYear = forecastDate.getUTCFullYear();
          const monthIndex = forecastDate.getUTCMonth(); // 0-11 (use UTC to match how we save)

          if (!sqlForecastsMap[accountId]) {
            sqlForecastsMap[accountId] = {};
          }

          if (!sqlForecastsMap[accountId][rowYear]) {
            sqlForecastsMap[accountId][rowYear] = {};
          }

          sqlForecastsMap[accountId][rowYear][monthIndex] = {
            forecastAmount: parseFloat(forecastRow.forecastAmount) || 0,
            driverInput: parseFloat(forecastRow.driverInput) || 0,
            driverType: forecastRow.driverType || '',
            id: forecastRow.id // Store ID for updates
          };
        });

        setForecastDetailData(detailsMap);
        setCosmosForecasts(sqlForecastsMap); // Reusing this state var but now it holds SQL data

        // Mark initial load as complete
        if (!hasInitiallyLoaded) {
          setHasInitiallyLoaded(true);
        }

        // Notify parent that refresh is complete (triggers success animation)
        if (isRefreshing && onRefreshComplete) {
          onRefreshComplete();
        }
      } catch (error) {
        console.error('Error fetching forecast P&L data:', error);
        // Still notify parent on error so spinner stops
        if (isRefreshing && onRefreshComplete) {
          onRefreshComplete();
        }
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchAllForecastDetails();
  }, [orgId, locationId, year, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Month names for column headers
  const allMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Filtered month names and indices based on date range
  const filteredMonthIndices = useMemo(() => {
    const indices = [];
    for (let i = startMonth; i <= endMonth; i++) {
      indices.push(i);
    }
    return indices;
  }, [startMonth, endMonth]);

  const monthNames = useMemo(() => {
    return filteredMonthIndices.map(i => allMonthNames[i]);
  }, [filteredMonthIndices]);

  // Group accounts hierarchically: AccountType > Department > Accounts
  const groupedAccounts = useMemo(() => {
    if (isLoadingDetails) {
      return { groups: {}, sortedTypes: [] };
    }
    const groups = {};

    chartOfAccounts.forEach(account => {
      const accountType = account.accountType || 'Uncategorized';

      // Only include Revenue and Expense accounts in P&L view
      if (accountType !== 'Revenue' && accountType !== 'Expense') {
        return;
      }

      // Filter out accounts with null department
      if (!account.department) {
        return;
      }

      const department = account.department;

      // Initialize account type group
      if (!groups[accountType]) {
        groups[accountType] = {
          type: accountType,
          departments: {},
          monthlyTotals: Array(12).fill(0), // Display totals (actual for past, forecast for future)
          monthlyDriverTotals: Array(12).fill(0), // Driver-based forecast totals for all periods
          monthlyBudgetTotals: Array(12).fill(0), // Budget totals
          monthlyCurrentPnLTotals: Array(12).fill(0), // Current P&L totals (actual for past, forecast for future)
          filteredMonthlyTotals: []
        };
      }

      // Initialize department group
      if (!groups[accountType].departments[department]) {
        groups[accountType].departments[department] = {
          name: department,
          accounts: [],
          monthlyTotals: Array(12).fill(0), // Display totals (actual for past, forecast for future)
          monthlyDriverTotals: Array(12).fill(0), // Driver-based forecast totals for all periods
          monthlyBudgetTotals: Array(12).fill(0), // Budget totals
          monthlyCurrentPnLTotals: Array(12).fill(0), // Current P&L totals (actual for past, forecast for future)
          filteredMonthlyTotals: []
        };
      }

      // Get forecast data for this account
      const accountForecasts = forecastData.filter(f => f.chartOfAccountId === account.id);
      const accountBudgets = budgetData.filter(b => b.chartOfAccountId === account.id);
      const driverType = forecastAccounts[account.id]?.forecastDriver || '';
      const driverInput = forecastAccounts[account.id]?.forecastInput || '';
      const monthlyDetails = forecastDetailData[account.id] || {};
      const cosmosForecastData = cosmosForecasts[account.id]?.[year] || {};

      // Calculate forecast for each month using pre-calculated values from V2 API
      // All driver calculations are done server-side using forecastCalculator.js
      const monthlyForecasts = [];
      const monthlyDriverForecasts = [];

      // Get current date for determining past vs future months
      const now = new Date();
      const currentMonth = now.getUTCMonth();
      const currentYear = now.getUTCFullYear();

      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const monthData = monthlyDetails[monthIndex] || {};
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

        // Get data from forecast detail view
        const rawBudget = monthData.budgetAmount || 0;
        const rawActual = monthData.actualAmount || 0;

        // Naturalize budget and actual amounts based on account type
        // Budget: For expenses, always multiply by -1 since budgets are stored as negative
        const budget = accountType === 'Expense'
          ? rawBudget * -1
          : (accountType === 'Revenue' && rawBudget < 0)
            ? Math.abs(rawBudget)
            : rawBudget;

        // Actual:
        // Revenue accounts: Multiply by -1 to flip sign (negative credit → positive, positive debit → negative)
        // Expense accounts: Use as-is (positive debit → positive, negative credit → negative)
        const actual = accountType === 'Revenue'
          ? rawActual * -1
          : rawActual;

        const availableRooms = monthData.roomsAvailable || 0;
        const occupiedRooms = monthData.roomsOccupied || 0;
        const daysWithData = monthData.roomStatsDaysWithData || 0;
        const importedRevenue = monthData.hotelRevenueAmount || 0;
        const otbForecastRevenue = monthData.otbForecastRevenue || 0;
        const rmsForecastRevenue = monthData.rmsForecastRevenue || 0;

        // Get previous month data for LM drivers
        const prevMonthIndex = getPreviousMonthIndex(monthIndex);
        const prevMonthData = monthlyDetails[prevMonthIndex] || {};
        const rawPreviousMonthActual = prevMonthData.actualAmount || 0;

        // Naturalize previous month actual for revenue accounts
        const previousMonthActual = accountType === 'Revenue' && rawPreviousMonthActual < 0
          ? Math.abs(rawPreviousMonthActual)
          : rawPreviousMonthActual;

        const previousMonthAvailableRooms = prevMonthData.roomsAvailable || 0;
        const previousMonthOccupiedRooms = prevMonthData.roomsOccupied || 0;

        // Get saved forecast from SQL
        const cosmosForecast = cosmosForecastData[monthIndex];

        // Determine if this is the current month or a past month
        const isCurrentMonth = (monthIndex === currentMonth && year === currentYear);
        const isPastMonth = (year < currentYear) || (year === currentYear && monthIndex < currentMonth);

        // USE PRE-CALCULATED FORECAST FROM V2 API
        // All calculations are done server-side using forecastCalculator.js
        // This ensures single source of truth for all driver calculations
        let calculatedForecast = monthData.calculatedForecastAmount;

        // Fallback for edge cases where calculatedForecastAmount might be missing
        if (calculatedForecast === undefined || calculatedForecast === null) {
          calculatedForecast = monthData.savedForecastAmount ?? monthData.hybridStaticEntryAmount ?? 0;
        }

        // Use calculated forecast if driver is auto-calculating, otherwise use saved value
        const driverBasedForecast = calculatedForecast !== null
          ? calculatedForecast
          : (cosmosForecast?.forecastAmount || 0);

        // Store the pure driver-based forecast for expanded Forecast column
        monthlyDriverForecasts.push(driverBasedForecast);

        // For collapsed view and totals: use actual for past months, forecast for current/future
        const displayAmount = isPastMonth ? actual : driverBasedForecast;
        monthlyForecasts.push(displayAmount);

        // Update monthly totals for department and account type
        // Display totals use actual for past, forecast for future (for collapsed view)
        groups[accountType].departments[department].monthlyTotals[monthIndex] += displayAmount;
        groups[accountType].monthlyTotals[monthIndex] += displayAmount;

        // Driver-based totals always use the driver forecast (for expanded Forecast column)
        groups[accountType].departments[department].monthlyDriverTotals[monthIndex] += driverBasedForecast;
        groups[accountType].monthlyDriverTotals[monthIndex] += driverBasedForecast;

        // Budget totals (for Budget column)
        groups[accountType].departments[department].monthlyBudgetTotals[monthIndex] += budget;
        groups[accountType].monthlyBudgetTotals[monthIndex] += budget;
      }

      // Calculate the sum of forecasts for the selected date range
      const totalInRange = filteredMonthIndices.reduce((sum, monthIdx) =>
        sum + (monthlyForecasts[monthIdx] || 0), 0
      );

      // Calculate monthly budgets and actuals for expanded view
      const monthlyBudgets = [];
      const monthlyActuals = [];
      const monthlyCurrentPnL = []; // Actual for past/current, Forecast for future

      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const monthData = monthlyDetails[monthIndex] || {};
        const rawBudget = monthData.budgetAmount || 0;
        const rawActual = monthData.actualAmount || 0;

        // Naturalize budget
        const budget = accountType === 'Expense'
          ? rawBudget * -1
          : (accountType === 'Revenue' && rawBudget < 0)
            ? Math.abs(rawBudget)
            : rawBudget;

        // Naturalize actual
        const actual = accountType === 'Revenue'
          ? rawActual * -1
          : rawActual;

        monthlyBudgets.push(budget);
        monthlyActuals.push(actual);

        // Determine if this is a past/current month or future month
        const now = new Date();
        const currentMonth = now.getUTCMonth();
        const currentYear = now.getUTCFullYear();
        const isPastOrCurrent = (year < currentYear) || (year === currentYear && monthIndex <= currentMonth);

        // For past/current months, use actual; for future months, use driver-based forecast
        const currentPnL = isPastOrCurrent ? actual : monthlyDriverForecasts[monthIndex];
        monthlyCurrentPnL.push(currentPnL);

        // Current P&L totals (for Current column)
        groups[accountType].departments[department].monthlyCurrentPnLTotals[monthIndex] += currentPnL;
        groups[accountType].monthlyCurrentPnLTotals[monthIndex] += currentPnL;
      }

      // Add account with its monthly data
      const accountData = {
        ...account,
        forecasts: accountForecasts,
        budgets: accountBudgets,
        monthlyForecasts: monthlyForecasts, // Display amounts (actual for past, forecast for future)
        monthlyDriverForecasts: monthlyDriverForecasts, // Pure driver-based forecast for all periods
        monthlyBudgets: monthlyBudgets,
        monthlyActuals: monthlyActuals,
        monthlyCurrentPnL: monthlyCurrentPnL,
        driverType: forecastAccounts[account.id]?.forecastDriver || '',
        driverInput: forecastAccounts[account.id]?.forecastInput || '',
        totalInRange: totalInRange
      };

      groups[accountType].departments[department].accounts.push(accountData);
    });

    // Calculate filtered monthly totals (only for the selected date range)
    Object.values(groups).forEach(typeGroup => {
      typeGroup.filteredMonthlyTotals = filteredMonthIndices.map(monthIdx =>
        typeGroup.monthlyTotals[monthIdx] || 0
      );

      Object.values(typeGroup.departments).forEach(deptGroup => {
        deptGroup.filteredMonthlyTotals = filteredMonthIndices.map(monthIdx =>
          deptGroup.monthlyTotals[monthIdx] || 0
        );

        // Filter out zero-balance accounts if hideZeroBalances is enabled
        // Account must have 0 across ALL of: budget, forecast, AND actual for the filtered date range
        if (hideZeroBalances) {
          deptGroup.accounts = deptGroup.accounts.filter(acc => {
            const budgetSum = filteredMonthIndices.reduce((sum, idx) => sum + (acc.monthlyBudgets[idx] || 0), 0);
            const forecastSum = filteredMonthIndices.reduce((sum, idx) => sum + (acc.monthlyForecasts[idx] || 0), 0);
            const actualSum = filteredMonthIndices.reduce((sum, idx) => sum + (acc.monthlyActuals[idx] || 0), 0);
            // Keep the account if ANY of budget, forecast, or actual is non-zero
            return budgetSum !== 0 || forecastSum !== 0 || actualSum !== 0;
          });
        }

        // Sort accounts alphabetically by account name
        deptGroup.accounts.sort((a, b) => {
          const nameA = (a.accountName || '').toLowerCase();
          const nameB = (b.accountName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });
    });

    // Sort account types: Revenue first, then Expenses, then others
    const sortedTypes = Object.keys(groups).sort((a, b) => {
      const order = { 'Revenue': 0, 'Expense': 1 };
      const aOrder = order[a] ?? 2;
      const bOrder = order[b] ?? 2;
      return aOrder - bOrder || a.localeCompare(b);
    });

    return { groups, sortedTypes };
  }, [chartOfAccounts, forecastData, budgetData, forecastAccounts, forecastDetailData, cosmosForecasts, isLoadingDetails, year, filteredMonthIndices, hideZeroBalances]);

  // Expand/Collapse All Handlers
  const expandAll = useCallback(() => {
    const newExpanded = {};
    groupedAccounts.sortedTypes.forEach(type => {
      newExpanded[type] = true;
      Object.keys(groupedAccounts.groups[type].departments).forEach(dept => {
        newExpanded[`${type}-${dept}`] = true;
      });
    });
    setExpandedSections(newExpanded);
  }, [groupedAccounts]);

  const collapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  // Expand to department level (default view) - shows departments but not individual accounts
  const expandToDepartmentLevel = useCallback(() => {
    const newExpanded = {};
    groupedAccounts.sortedTypes.forEach(type => {
      newExpanded[type] = true; // Expand account types (Revenue, Expense)
      // Don't expand departments - this keeps individual accounts hidden
    });
    setExpandedSections(newExpanded);
  }, [groupedAccounts]);

  // Set initial expansion to department level on mount
  React.useEffect(() => {
    if (groupedAccounts.sortedTypes.length > 0) {
      expandToDepartmentLevel();
    }
  }, [groupedAccounts.sortedTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Escape key to exit full screen mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen]);

  // Calculate net income (Revenue - Expenses) for filtered months
  const netIncomeMonthly = useMemo(() => {
    const revenueMonthly = groupedAccounts.groups['Revenue']?.filteredMonthlyTotals || [];
    const expensesMonthly = groupedAccounts.groups['Expense']?.filteredMonthlyTotals || [];
    return revenueMonthly.map((rev, idx) => rev - (expensesMonthly[idx] || 0));
  }, [groupedAccounts]);

  // Calculate total net income for the filtered date range
  const totalNetIncome = useMemo(() => {
    return netIncomeMonthly.reduce((sum, val) => sum + val, 0);
  }, [netIncomeMonthly]);

  // Toggle section expansion
  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  // Toggle column expansion
  const toggleColumnExpansion = useCallback((monthIndex) => {
    setExpandedColumns(prev => ({
      ...prev,
      [monthIndex]: !prev[monthIndex]
    }));
  }, []);

  // Handle account click to show detail modal
  const handleAccountClick = useCallback((account) => {
    setSelectedAccount(account);
    setShowDetailModal(true);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedAccount(null);
  }, []);

  // Refetch forecast data
  const refetchForecastData = useCallback(async () => {
    try {
      const [allData, allForecasts] = await Promise.all([
        getForecastPnLDataV2({ orgId, locationId, year }),
        getFinancialForecasts({ orgId, locationId, year })
      ]);

      // Rebuild maps
      const detailsMap = {};
      allData.forEach(entry => {
        const accountId = entry.chartOfAccountId;
        const monthIndex = entry.monthNumber - 1;
        if (!detailsMap[accountId]) detailsMap[accountId] = {};
        detailsMap[accountId][monthIndex] = entry;
      });

      const sqlForecastsMap = {};
      allForecasts.forEach(forecastRow => {
        const accountId = forecastRow.chartOfAccountId;
        const forecastDate = new Date(forecastRow.forecastDate);
        const rowYear = forecastDate.getUTCFullYear();
        const monthIndex = forecastDate.getUTCMonth();

        if (!sqlForecastsMap[accountId]) sqlForecastsMap[accountId] = {};
        if (!sqlForecastsMap[accountId][rowYear]) sqlForecastsMap[accountId][rowYear] = {};

        sqlForecastsMap[accountId][rowYear][monthIndex] = {
          forecastAmount: parseFloat(forecastRow.forecastAmount) || 0,
          driverInput: parseFloat(forecastRow.driverInput) || 0,
          driverType: forecastRow.driverType || '',
          id: forecastRow.id
        };
      });

      setForecastDetailData(detailsMap);
      setCosmosForecasts(sqlForecastsMap);
    } catch (error) {
      console.error('Error refetching forecast data:', error);
    }
  }, [orgId, locationId, year]);

  // Handle data change from modal (no longer closes modal on data changes)
  const handleDetailDataChange = useCallback(async () => {
    // Refetch local forecast data to get updated driverInput values
    await refetchForecastData();

    // Notify parent to reload data
    if (onDataChange) {
      await onDataChange();
    }
    // Note: Modal stays open - user must explicitly close it
  }, [onDataChange, refetchForecastData]);

  // Handle inline cell edit with debounced save
  const handleCellEdit = useCallback(async (accountId, monthIndex, newValue, driverType) => {
    const cellKey = `${accountId}_${monthIndex}`;

    // Update local state immediately
    setLocalEdits(prev => ({ ...prev, [cellKey]: newValue }));

    // Show saving indicator
    setSavingCells(prev => new Set([...prev, cellKey]));

    // Clear existing timer
    if (saveTimerRef.current[cellKey]) {
      clearTimeout(saveTimerRef.current[cellKey]);
    }

    // Set new timer to save after 800ms
    saveTimerRef.current[cellKey] = setTimeout(async () => {
      try {
        // Create date as first day of the month
        const forecastDate = new Date(year, monthIndex, 1);

        // First, clear ALL existing forecast entries for this account/month
        // This ensures monthly entry replaces any existing daily entries
        await clearForecastMonth({
          orgId,
          locationId,
          chartOfAccountId: accountId,
          year: year,
          month: monthIndex + 1  // API expects 1-12, monthIndex is 0-11
        });

        // SQL endpoint expects: orgId, locationId, chartOfAccountId, forecastDate,
        // driverType, driverInput, forecastAmount
        await createFinancialForecast({
          orgId,
          locationId,
          chartOfAccountId: accountId,
          forecastDate: forecastDate.toISOString(),
          driverType: driverType || 'Static Entry',
          driverInput: 0,
          forecastAmount: newValue
        });

        // Refetch data to update totals
        await refetchForecastData();

        // Mark as saved and remove saving indicator
        setSavedCells(prev => new Set([...prev, cellKey]));
        setSavingCells(prev => {
          const updated = new Set(prev);
          updated.delete(cellKey);
          return updated;
        });

        // Clear saved status after delay
        setTimeout(() => {
          setSavedCells(prev => {
            const updated = new Set(prev);
            updated.delete(cellKey);
            return updated;
          });
        }, 2000);

      } catch (error) {
        console.error('Error saving forecast:', error);
        setSavingCells(prev => {
          const updated = new Set(prev);
          updated.delete(cellKey);
          return updated;
        });
      } finally {
        delete saveTimerRef.current[cellKey];
      }
    }, 800);
  }, [orgId, locationId, year, refetchForecastData]);

  // Handle opening daily forecast modal
  const handleOpenDailyModal = useCallback((account, monthIndex) => {
    setDailyModalAccount(account);
    setDailyModalMonth(monthIndex);
    setShowDailyModal(true);
  }, []);

  // Handle closing daily forecast modal
  const handleCloseDailyModal = useCallback(() => {
    setShowDailyModal(false);
    setDailyModalAccount(null);
    setDailyModalMonth(null);
  }, []);

  // Handle daily modal save
  const handleDailyModalSave = useCallback(async () => {
    await refetchForecastData();
    handleCloseDailyModal();
  }, [refetchForecastData, handleCloseDailyModal]);

  // Handle account selection
  const handleSelectAccount = useCallback((accountId) => {
    setSelectedAccountIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  }, []);

  // Handle group selection (Department or Account Type)
  const handleSelectGroup = useCallback((accounts) => {
    const accountIds = accounts.map(a => a.id);
    setSelectedAccountIds(prev => {
      const newSet = new Set(prev);
      const allSelected = accountIds.every(id => newSet.has(id));
      
      if (allSelected) {
        // Deselect all
        accountIds.forEach(id => newSet.delete(id));
      } else {
        // Select all
        accountIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  }, []);

  // Handle bulk save
  const handleBulkSave = async (driverSettings) => {
    try {
      const accountsToUpdate = Array.from(selectedAccountIds).map(accountId => ({
        orgId,
        locationId,
        chartOfAccountId: accountId,
        forecastDriver: driverSettings.driverType,
        forecastInput: driverSettings.driverInput,
        inputMode: 'monthly'
      }));

      await upsertBulkForecastAccounts(accountsToUpdate);
      
      // Refresh data
      await refetchForecastData();
      if (onDataChange) await onDataChange();
      
      // Reset selection
      setSelectedAccountIds(new Set());
      setSelectionMode(false);
    } catch (error) {
      console.error('Error updating bulk drivers:', error);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return formatCurrencyRounded(0, currency);
    const formatted = formatCurrencyRounded(Math.abs(value), currency);
    return value < 0 ? `(${formatted})` : formatted;
  };

  // Show loading state only on initial load (not during background refresh)
  if (isLoadingDetails && !hasInitiallyLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading forecast data...</p>
        </div>
      </div>
    );
  }

  const content = (
    <div className={`flex flex-col bg-white border rounded-lg overflow-hidden transition-all duration-300 ${
      isFullScreen ? 'fixed inset-0 z-[9999] rounded-none' : 'h-full'
    }`}>
      {/* Compact Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/50">
        {/* Left: View Controls */}
        <div className="flex items-center gap-2">
          {/* Auto-save Indicator */}
          <div className="flex items-center px-2 py-0.5 bg-white rounded border border-gray-100">
            {savingCells.size > 0 ? (
              <div className="flex items-center gap-1 text-[10px] text-orange-600 font-medium">
                <Loader2 size={10} className="animate-spin" />
                <span>Saving</span>
              </div>
            ) : savedCells.size > 0 ? (
              <div className="flex items-center gap-1 text-[10px] text-green-600 font-medium">
                <Check size={10} />
                <span>Saved</span>
              </div>
            ) : (
              <span className="text-[10px] text-gray-400 font-medium">Synced</span>
            )}
          </div>

          <div className="h-4 w-px bg-gray-200" />

          {/* Expand/Collapse */}
          <div className="flex items-center gap-0.5 bg-white rounded border border-gray-200">
            <button
              onClick={expandAll}
              className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-l transition-colors"
              title="Expand All"
            >
              <Maximize2 size={12} />
            </button>
            <button
              onClick={collapseAll}
              className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-r transition-colors"
              title="Collapse All"
            >
              <Minimize2 size={12} />
            </button>
          </div>

          {/* Density Toggle */}
          <button
            onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
            className={`p-1 rounded border transition-colors ${
              density === 'compact'
                ? 'bg-orange-50 text-orange-600 border-orange-200'
                : 'bg-white text-gray-500 border-gray-200 hover:text-gray-900 hover:bg-gray-50'
            }`}
            title={density === 'compact' ? "Comfortable View" : "Compact View"}
          >
            <Layout size={12} />
          </button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Selection Mode Toggle */}
          <button
            onClick={() => {
              setSelectionMode(!selectionMode);
              if (selectionMode) setSelectedAccountIds(new Set());
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition-colors ${
              selectionMode
                ? 'bg-orange-50 border-orange-200 text-orange-600'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {selectionMode ? <CheckSquare size={12} /> : <Square size={12} />}
            <span className="hidden sm:inline">{selectionMode ? 'Done' : 'Select'}</span>
          </button>

          {/* Filter Controls */}
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-xs font-medium hover:bg-gray-50 transition-colors text-gray-600"
            >
              <Filter size={12} />
              <span className="hidden sm:inline">Filters</span>
              {(startMonth !== 0 || endMonth !== 11 || hideZeroBalances) && (
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-white text-[8px] font-bold">
                  {[startMonth !== 0 || endMonth !== 11, hideZeroBalances].filter(Boolean).length}
                </span>
              )}
            </button>
            {showFilterDropdown && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-4 animate-in fade-in zoom-in-95 duration-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">View Options</h3>
                  <button
                    onClick={() => setShowFilterDropdown(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* Date Range Filter */}
                <div className="mb-5">
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Date Range</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <select
                        value={startMonth}
                        onChange={(e) => {
                          const newStart = parseInt(e.target.value);
                          setStartMonth(newStart);
                          if (newStart > endMonth) {
                            setEndMonth(newStart);
                          }
                        }}
                        className="w-full pl-2 pr-6 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
                      >
                        {allMonthNames.map((month, idx) => (
                          <option key={idx} value={idx}>{month}</option>
                        ))}
                      </select>
                      <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    <span className="text-gray-400 text-xs">to</span>
                    <div className="relative flex-1">
                      <select
                        value={endMonth}
                        onChange={(e) => {
                          const newEnd = parseInt(e.target.value);
                          setEndMonth(newEnd);
                          if (newEnd < startMonth) {
                            setStartMonth(newEnd);
                          }
                        }}
                        className="w-full pl-2 pr-6 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
                      >
                        {allMonthNames.map((month, idx) => (
                          <option key={idx} value={idx}>{month}</option>
                        ))}
                      </select>
                      <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Hide Zero Balances Toggle */}
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <div className={`p-1.5 rounded-md ${hideZeroBalances ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
                      <EyeOff size={14} />
                    </div>
                    Hide Zero Balance Accounts
                  </label>
                  <button
                    onClick={() => setHideZeroBalances(!hideZeroBalances)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      hideZeroBalances ? 'bg-orange-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                        hideZeroBalances ? 'translate-x-4.5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Clear Filters Button */}
                {(startMonth !== 0 || endMonth !== 11 || hideZeroBalances) && (
                  <button
                    onClick={() => {
                      setStartMonth(0);
                      setEndMonth(11);
                      setHideZeroBalances(false);
                    }}
                    className="w-full py-2 text-xs text-gray-600 hover:text-gray-900 font-medium bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Reset to Default
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Full Screen */}
          <button
            onClick={() => setIsFullScreen(!isFullScreen)}
            className={`p-1 rounded border transition-colors ${
              isFullScreen
                ? 'bg-orange-50 text-orange-600 border-orange-200'
                : 'bg-white text-gray-500 border-gray-200 hover:text-gray-900 hover:bg-gray-50'
            }`}
            title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
          >
            {isFullScreen ? <Minimize size={12} /> : <Maximize size={12} />}
          </button>

          {/* Net Income Display */}
          <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-gray-200">
            <span className="text-[10px] text-gray-400 font-medium uppercase">Net:</span>
            <span className={`text-sm font-bold ${totalNetIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(totalNetIncome)}
            </span>
          </div>
        </div>
      </div>

      {/* P&L Table */}
      <div className="flex-1 overflow-auto bg-white relative">
        <table className="min-w-full border-collapse text-[13px]">
          <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b border-r border-gray-200 sticky left-0 bg-gray-50 z-30 w-[280px] min-w-[200px]">
                <div className="flex items-center gap-2">
                  {selectionMode && <div className="w-4" />}
                  Account
                </div>
              </th>
              <th className="text-center px-2 py-2 font-semibold text-gray-700 border-b border-r border-gray-200 w-24">Driver</th>
              {filteredMonthIndices.map((monthIdx, displayIdx) => {
                const isExpanded = expandedColumns[monthIdx];
                const now = new Date();
                const currentMonth = now.getUTCMonth();
                const currentYear = now.getUTCFullYear();
                const isPastMonth = (year < currentYear) || (year === currentYear && monthIdx < currentMonth);
                const periodType = isPastMonth ? 'ACT' : 'FCST';

                return (
                  <th
                    key={displayIdx}
                    colSpan={isExpanded ? 5 : 1}
                    className={`text-center px-1 py-1.5 font-semibold text-gray-700 border-b border-r border-gray-200 transition-all duration-200 ${
                      isExpanded ? 'bg-orange-50/50' : ''
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <div className="flex items-center justify-center gap-1 w-full relative group cursor-pointer" onClick={() => toggleColumnExpansion(monthIdx)}>
                        <span className="uppercase tracking-wide text-xs">{allMonthNames[monthIdx]}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleColumnExpansion(monthIdx);
                          }}
                          className="p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-all"
                          title={isExpanded ? "Collapse details" : "Expand details"}
                        >
                          {isExpanded ? <Minus size={12} /> : <Plus size={12} />}
                        </button>
                      </div>
                      {!isExpanded && (
                        <span className={`text-[9px] px-1 rounded-sm font-bold tracking-tighter ${
                          isPastMonth
                            ? 'text-blue-600/80'
                            : 'text-purple-600/80'
                        }`}>
                          {periodType}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              {/* Total Column Header */}
              <th className="text-center px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 bg-gray-100 min-w-[100px]">
                <div className="flex flex-col items-center justify-center gap-0.5">
                  <span className="uppercase tracking-wide text-xs">Total</span>
                </div>
              </th>
            </tr>
            {/* Sub-headers for expanded columns */}
            {Object.keys(expandedColumns).some(key => expandedColumns[key]) && (
              <tr className="bg-gray-50/80 text-[10px] text-gray-500 font-medium border-b border-gray-200">
                <th className="sticky left-0 bg-gray-50 z-30 border-r border-gray-200"></th>
                <th className="border-r border-gray-200"></th>
                {filteredMonthIndices.map((monthIdx, displayIdx) => {
                  const isExpanded = expandedColumns[monthIdx];
                  if (isExpanded) {
                    return (
                      <React.Fragment key={displayIdx}>
                        <th className="px-1 py-1 border-r border-gray-200 bg-orange-50/30 text-center w-20">Current</th>
                        <th className="px-1 py-1 border-r border-gray-200 bg-orange-50/30 text-center w-20 font-bold text-gray-700">Forecast</th>
                        <th className="px-1 py-1 border-r border-gray-200 bg-orange-50/30 text-center w-16">Var</th>
                        <th className="px-1 py-1 border-r border-gray-200 bg-orange-50/30 text-center w-20">Budget</th>
                        <th className="px-1 py-1 border-r border-gray-200 bg-orange-50/30 text-center w-16">vs Bud</th>
                      </React.Fragment>
                    );
                  } else {
                    return <th key={displayIdx} className="border-r border-gray-200"></th>;
                  }
                })}
                <th className="border-gray-200 bg-gray-100"></th>
              </tr>
            )}
          </thead>
          <tbody className="bg-white">
            {groupedAccounts.sortedTypes.map(accountType => {
              const typeData = groupedAccounts.groups[accountType];
              const isTypeExpanded = expandedSections[accountType];

              return (
                <React.Fragment key={accountType}>
                  {/* Account Type Header */}
                  <tr
                    className={`bg-gray-100 hover:bg-gray-200/80 cursor-pointer border-b border-gray-200 transition-colors ${
                      density === 'compact' ? 'h-7 text-xs' : 'h-9 text-sm'
                    }`}
                    onClick={() => toggleSection(accountType)}
                  >
                    <td className={`font-bold text-gray-800 flex items-center sticky left-0 bg-gray-100 z-10 border-r border-gray-300 pl-3 ${
                      density === 'compact' ? 'py-1' : 'py-2'
                    }`}>
                      {selectionMode && (
                        <div onClick={(e) => {
                          e.stopPropagation();
                          const allAccounts = Object.values(typeData.departments).flatMap(d => d.accounts);
                          handleSelectGroup(allAccounts);
                        }} className="mr-2">
                          <input 
                            type="checkbox" 
                            checked={Object.values(typeData.departments).flatMap(d => d.accounts).every(a => selectedAccountIds.has(a.id))}
                            readOnly
                            className="rounded border-gray-400 text-orange-600 focus:ring-orange-500 w-3.5 h-3.5"
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
                         <div className="text-gray-400">
                           {isTypeExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                         </div>
                         <span className="truncate">{accountType}</span>
                      </div>
                    </td>
                    <td className="border-r border-gray-300"></td>
                    {filteredMonthIndices.map((monthIdx, displayIdx) => {
                      const isExpanded = expandedColumns[monthIdx];
                      const amount = typeData.monthlyTotals[monthIdx];
                      const currentPnLTotal = typeData.monthlyCurrentPnLTotals[monthIdx];
                      const driverTotal = typeData.monthlyDriverTotals[monthIdx];
                      const budgetTotal = typeData.monthlyBudgetTotals[monthIdx];
                      const varianceFromCurrent = currentPnLTotal - driverTotal;
                      const varianceFromBudget = currentPnLTotal - budgetTotal;

                      const cellClass = `px-1.5 text-right font-bold text-gray-900 border-r border-gray-300 ${
                        density === 'compact' ? 'py-1 text-[11px]' : 'py-2 text-xs'
                      }`;

                      if (isExpanded) {
                        return (
                          <React.Fragment key={displayIdx}>
                            <td className={`${cellClass} bg-gray-50`}>{formatCurrency(currentPnLTotal)}</td>
                            <td className={`${cellClass} bg-gray-50`}>{formatCurrency(driverTotal)}</td>
                            <td className={`${cellClass} bg-gray-50 ${varianceFromCurrent >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(varianceFromCurrent)}</td>
                            <td className={`${cellClass} bg-gray-50`}>{formatCurrency(budgetTotal)}</td>
                            <td className={`${cellClass} bg-gray-50 ${varianceFromBudget >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(varianceFromBudget)}</td>
                          </React.Fragment>
                        );
                      } else {
                        return (
                          <td key={displayIdx} className={cellClass}>
                            {formatCurrency(amount)}
                          </td>
                        );
                      }
                    })}
                    {/* Total cell for account type */}
                    <td className={`px-2 text-right font-bold text-gray-900 bg-gray-100 ${
                      density === 'compact' ? 'py-1 text-[11px]' : 'py-2 text-xs'
                    }`}>
                      {formatCurrency(typeData.filteredMonthlyTotals.reduce((sum, val) => sum + val, 0))}
                    </td>
                  </tr>

                  {/* Departments within Account Type */}
                  {isTypeExpanded && Object.entries(typeData.departments).map(([deptName, deptData]) => {
                    const deptKey = `${accountType}-${deptName}`;
                    const isDeptExpanded = expandedSections[deptKey];

                    return (
                      <React.Fragment key={deptKey}>
                        {/* Department Header */}
                        <tr
                          className={`bg-gray-50 hover:bg-gray-100 cursor-pointer border-b border-gray-200/80 transition-colors ${
                            density === 'compact' ? 'h-6 text-[11px]' : 'h-8 text-xs'
                          }`}
                          onClick={() => toggleSection(deptKey)}
                        >
                          <td className={`font-semibold text-gray-700 flex items-center sticky left-0 bg-gray-50 z-10 border-r border-gray-200 pl-6 ${
                            density === 'compact' ? 'py-0.5' : 'py-1.5'
                          }`}>
                            {selectionMode && (
                              <div onClick={(e) => {
                                e.stopPropagation();
                                handleSelectGroup(deptData.accounts);
                              }} className="mr-2">
                                <input 
                                  type="checkbox" 
                                  checked={deptData.accounts.every(a => selectedAccountIds.has(a.id))}
                                  readOnly
                                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 w-3 h-3"
                                />
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
                              <div className="text-gray-400">
                                {isDeptExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </div>
                              <span className="truncate">{deptName}</span>
                            </div>
                          </td>
                          <td className="border-r border-gray-200"></td>
                          {filteredMonthIndices.map((monthIdx, displayIdx) => {
                            const isExpanded = expandedColumns[monthIdx];
                            const amount = deptData.monthlyTotals[monthIdx];
                            const currentPnLTotal = deptData.monthlyCurrentPnLTotals[monthIdx];
                            const driverTotal = deptData.monthlyDriverTotals[monthIdx];
                            const budgetTotal = deptData.monthlyBudgetTotals[monthIdx];
                            const varianceFromCurrent = currentPnLTotal - driverTotal;
                            const varianceFromBudget = currentPnLTotal - budgetTotal;

                            const cellClass = `px-1.5 text-right font-semibold text-gray-700 border-r border-gray-200 ${
                              density === 'compact' ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]'
                            }`;

                            if (isExpanded) {
                              return (
                                <React.Fragment key={displayIdx}>
                                  <td className={cellClass}>{formatCurrency(currentPnLTotal)}</td>
                                  <td className={cellClass}>{formatCurrency(driverTotal)}</td>
                                  <td className={`${cellClass} ${varianceFromCurrent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varianceFromCurrent)}</td>
                                  <td className={cellClass}>{formatCurrency(budgetTotal)}</td>
                                  <td className={`${cellClass} ${varianceFromBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varianceFromBudget)}</td>
                                </React.Fragment>
                              );
                            } else {
                              return (
                                <td key={displayIdx} className={cellClass}>
                                  {formatCurrency(amount)}
                                </td>
                              );
                            }
                          })}
                          {/* Total cell for department */}
                          <td className={`px-2 text-right font-semibold text-gray-700 bg-gray-50 ${
                            density === 'compact' ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]'
                          }`}>
                            {formatCurrency(deptData.filteredMonthlyTotals.reduce((sum, val) => sum + val, 0))}
                          </td>
                        </tr>

                        {/* Accounts within Department */}
                        {isDeptExpanded && deptData.accounts.map(account => {
                          const isStaticEntry = account.driverType === 'Static Entry' || !account.driverType;

                          return (
                            <tr
                              key={account.id}
                              className={`group hover:bg-orange-50/30 transition-colors border-b border-gray-100 ${
                                density === 'compact' ? 'h-6 text-[11px]' : 'h-8 text-[12px]'
                              }`}
                            >
                              <td
                                className={`text-gray-600 sticky left-0 bg-white group-hover:bg-orange-50/30 z-10 cursor-pointer border-r border-gray-200 pl-10 ${
                                  density === 'compact' ? 'py-0.5' : 'py-1'
                                }`}
                                onClick={() => {
                                  if (selectionMode) {
                                    handleSelectAccount(account.id);
                                  } else {
                                    handleAccountClick(account);
                                  }
                                }}
                              >
                                <div className="flex items-center h-full">
                                  {selectionMode && (
                                    <div className="mr-2 flex-shrink-0">
                                      <input 
                                        type="checkbox" 
                                        checked={selectedAccountIds.has(account.id)}
                                        onChange={() => {}} 
                                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 w-3 h-3"
                                      />
                                    </div>
                                  )}
                                  <div className="flex flex-col justify-center min-w-0">
                                    <div className="font-medium truncate text-gray-700">{account.accountName}</div>
                                    <div className="text-[9px] text-gray-400 font-mono leading-none mt-0.5">{account.accountNumber}</div>
                                  </div>
                                </div>
                              </td>
                              <td className={`px-1 text-center border-r border-gray-200 ${density === 'compact' ? 'py-0.5' : 'py-1'}`}>
                                {account.driverType && (
                                  <span className="inline-block px-1.5 py-0.5 rounded text-[9px] bg-gray-100 text-gray-600 truncate max-w-[80px]" title={account.driverType}>
                                    {account.driverType}
                                  </span>
                                )}
                              </td>
                              {filteredMonthIndices.map((monthIdx, displayIdx) => {
                                const isExpanded = expandedColumns[monthIdx];
                                const forecastAmount = account.monthlyForecasts[monthIdx];
                                const driverForecastAmount = account.monthlyDriverForecasts[monthIdx];
                                const currentPnL = account.monthlyCurrentPnL[monthIdx];
                                const budgetAmount = account.monthlyBudgets[monthIdx];
                                const varianceFromCurrent = currentPnL - driverForecastAmount;
                                const varianceFromBudget = currentPnL - budgetAmount;

                                const now = new Date();
                                const currentMonth = now.getUTCMonth();
                                const currentYear = now.getUTCFullYear();
                                const isPastMonth = (year < currentYear) || (year === currentYear && monthIdx < currentMonth);

                                const cellKey = `${account.id}_${monthIdx}`;
                                const displayValue = localEdits[cellKey] !== undefined ? localEdits[cellKey] : (isExpanded ? driverForecastAmount : forecastAmount);
                                const isSaving = savingCells.has(cellKey);
                                const isSaved = savedCells.has(cellKey);

                                // Base cell styles
                                const baseCellClass = `px-1.5 text-right border-r border-gray-100 group-hover:border-gray-200 ${
                                  density === 'compact' ? 'py-0.5' : 'py-1'
                                }`;

                                if (isExpanded) {
                                  return (
                                    <React.Fragment key={displayIdx}>
                                      <td className={`${baseCellClass} text-gray-500`}>{formatCurrency(currentPnL)}</td>
                                      
                                      {/* Editable Forecast Cell */}
                                      <td className={`${baseCellClass} relative p-0`}>
                                        {isStaticEntry ? (
                                          <div className="h-full w-full relative">
                                            <input
                                              type="number"
                                              value={displayValue}
                                              onChange={(e) => handleCellEdit(account.id, monthIdx, parseFloat(e.target.value) || 0, account.driverType)}
                                              onFocus={(e) => {
                                                if (parseFloat(e.target.value) === 0) setLocalEdits(prev => ({ ...prev, [cellKey]: '' }));
                                              }}
                                              onBlur={(e) => {
                                                if (e.target.value === '') handleCellEdit(account.id, monthIdx, 0, account.driverType);
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                              className={`w-full h-full text-right px-1.5 bg-transparent focus:bg-white border-0 border-b-2 border-transparent focus:border-orange-500 focus:outline-none transition-colors font-medium text-gray-800 ${
                                                isSaving ? 'text-orange-600' : isSaved ? 'text-green-600' : ''
                                              } placeholder-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                            />
                                            {isSaving && <Loader2 size={10} className="absolute top-1 left-1 animate-spin text-orange-400" />}
                                          </div>
                                        ) : (
                                          <span className="font-medium text-gray-800">{formatCurrency(displayValue)}</span>
                                        )}
                                      </td>
                                      
                                      <td className={`${baseCellClass} ${varianceFromCurrent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varianceFromCurrent)}</td>
                                      <td className={`${baseCellClass} text-gray-500`}>{formatCurrency(budgetAmount)}</td>
                                      <td className={`${baseCellClass} ${varianceFromBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(varianceFromBudget)}</td>
                                    </React.Fragment>
                                  );
                                } else {
                                  return (
                                    <td key={displayIdx} className={`${baseCellClass} relative p-0`}>
                                      {isStaticEntry && !isPastMonth ? (
                                        <div className="h-full w-full relative group/input">
                                          <input
                                            type="number"
                                            value={displayValue}
                                            onChange={(e) => handleCellEdit(account.id, monthIdx, parseFloat(e.target.value) || 0, account.driverType)}
                                            onFocus={(e) => {
                                              if (parseFloat(e.target.value) === 0) setLocalEdits(prev => ({ ...prev, [cellKey]: '' }));
                                            }}
                                            onBlur={(e) => {
                                              if (e.target.value === '') handleCellEdit(account.id, monthIdx, 0, account.driverType);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className={`w-full h-full text-right px-1.5 bg-transparent hover:bg-white focus:bg-white border-transparent focus:border-b-2 focus:border-orange-500 focus:ring-0 transition-colors font-medium text-gray-800 ${
                                              isSaving ? 'text-orange-600' : isSaved ? 'text-green-600' : ''
                                            } [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none cursor-pointer`}
                                          />
                                          {isSaving && <Loader2 size={8} className="absolute top-1/2 left-1 -translate-y-1/2 animate-spin text-orange-400 pointer-events-none" />}
                                          
                                          {/* Hover Daily Modal Trigger */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenDailyModal(account, monthIdx);
                                            }}
                                            className="opacity-0 group-hover/input:opacity-100 absolute top-1/2 -translate-y-1/2 left-0 p-0.5 hover:bg-orange-100 text-gray-400 hover:text-orange-600 rounded transition-all"
                                            tabIndex={-1}
                                          >
                                            <ArrowRightLeft size={10} />
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="h-full w-full px-1.5 flex items-center justify-end font-medium text-gray-800 bg-gray-50/50">
                                          {formatCurrency(displayValue)}
                                          {!isPastMonth && (account.driverType === 'OTB Forecast' || account.driverType === 'RMS Forecast') && (
                                             <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDailyModal(account, monthIdx);
                                              }}
                                              className="ml-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-orange-600 transition-opacity"
                                            >
                                              <ArrowRightLeft size={10} />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }
                              })}
                              {/* Total cell for individual account */}
                              <td className={`px-2 text-right font-medium text-gray-700 bg-white group-hover:bg-orange-50/30 ${
                                density === 'compact' ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]'
                              }`}>
                                {formatCurrency(account.totalInRange)}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Net Income Row */}
            <tr className="bg-white border-t-2 border-gray-300 font-bold sticky bottom-0 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <td className={`text-gray-900 sticky left-0 bg-white z-40 border-r border-gray-200 pl-4 ${
                density === 'compact' ? 'py-1.5 text-xs' : 'py-3 text-sm'
              }`}>
                Net Income
              </td>
              <td className="border-r border-gray-200"></td>
              {filteredMonthIndices.map((monthIdx, displayIdx) => {
                const isExpanded = expandedColumns[monthIdx];
                const amount = netIncomeMonthly[displayIdx];
                const cellClass = `text-right px-1.5 border-r border-gray-200 ${
                  density === 'compact' ? 'py-1.5 text-xs' : 'py-3 text-sm'
                }`;

                if (isExpanded) {
                  return (
                    <React.Fragment key={displayIdx}>
                      <td className={`${cellClass} ${amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(amount)}</td>
                      <td className={`${cellClass} ${amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(amount)}</td>
                      <td className={cellClass}>-</td>
                      <td className={cellClass}>-</td>
                      <td className={cellClass}>-</td>
                    </React.Fragment>
                  );
                } else {
                  return (
                    <td key={displayIdx} className={`${cellClass} ${amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(amount)}
                    </td>
                  );
                }
              })}
              {/* Total cell for Net Income */}
              <td className={`text-right px-2 bg-gray-100 ${
                density === 'compact' ? 'py-1.5 text-xs' : 'py-3 text-sm'
              } ${totalNetIncome >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatCurrency(totalNetIncome)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bulk Actions Bar */}
      {selectedAccountIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-orange-200 shadow-lg rounded-lg px-6 py-3 z-50 flex items-center gap-4 animate-slide-up">
          <div className="text-sm font-semibold text-gray-900">
            {selectedAccountIds.size} accounts selected
          </div>
          <div className="h-6 w-px bg-gray-200"></div>
          <button
            onClick={() => setShowBulkModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors shadow-sm"
          >
            <Edit size={16} />
            Edit Drivers
          </button>
          <button
            onClick={() => setSelectedAccountIds(new Set())}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Bulk Driver Edit Modal */}
      <BulkDriverEditModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedAccounts={Array.from(selectedAccountIds)}
        onSave={handleBulkSave}
      />

      {/* Detail Modal */}
      {showDetailModal && selectedAccount && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedAccount.accountName}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedAccount.accountNumber} • {selectedAccount.accountType}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Forecast Detail */}
            <div className="flex-1 overflow-hidden p-6">
              <ForecastAccountDetail
                account={{
                  ...selectedAccount,
                  chartOfAccountId: selectedAccount.id,
                  locationId: locationId
                }}
                forecastData={forecastData}
                budgetData={budgetData}
                orgId={orgId}
                locationId={locationId}
                currency={currency}
                year={year}
                onDataChange={handleDetailDataChange}
                categoryDriverInfo={{}}
                forecastDriverExplanations={{
                  'Static Entry': 'Forecast value is manually entered for each month. If no value is entered, it may pull from the budget.',
                  '$ / ORN': 'Forecast is calculated by multiplying a user-defined rate by the number of Occupied Room Nights for each month.',
                  '$ / ARN': 'Forecast is calculated by multiplying a user-defined rate by the number of Available Room Nights for each month.',
                  'Total Guests': 'Forecast is calculated by multiplying a user-defined driver value by the number of guests for each month.',
                  '$ / ARN LM': "Forecast is based on last month's actual calculated dollars per Available Room Night.",
                  '$ / ORN LM': "Forecast is based on last month's actual calculated dollars per Occupied Room Night.",
                  '$ LM': "Forecast uses the total actual booked amount from the previous month.",
                  'Budget': 'Uses the budgeted values for each month.',
                  'OTB Forecast': 'On-The-Books forecast revenue from hotel occupancy data.',
                  'RMS Forecast': 'Revenue Management System forecast revenue from hotel occupancy data.'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Daily Forecast Modal */}
      {showDailyModal && dailyModalAccount && dailyModalMonth !== null && (
        <DailyForecastModal
          isOpen={showDailyModal}
          onClose={handleCloseDailyModal}
          account={{
            ...dailyModalAccount,
            chartOfAccountId: dailyModalAccount.id,
            locationId: locationId
          }}
          month={dailyModalMonth}
          year={year}
          orgId={orgId}
          locationId={locationId}
          currency={currency}
          selectedDriver={dailyModalAccount.driverType || 'Static Entry'}
          onSave={handleDailyModalSave}
        />
      )}
    </div>
  );

  if (isFullScreen) {
    return createPortal(content, document.body);
  }

  return content;
};

export default React.memo(PnLForecastView);
